import React from 'react';

import ListingBody from '@plone/volto/components/manage/Blocks/Listing/ListingBody';
import { withBlockExtensions } from '@plone/volto/helpers/Extensions';

import config from '@plone/volto/registry';

import {
  withSearch,
  withQueryString,
} from '@plone/volto/components/manage/Blocks/Search/hocs';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { gaEvent } from '../../../../../helpers/analytics';
import { SORT_DEFAULTS } from '../../../../../config/searchRegistry.generated';
import isFunction from 'lodash/isFunction';
import cx from 'classnames';

/**
 * SHADOW of @plone/volto SearchBlockView.
 *
 * Two changes vs core. (2) is `withControls` below — the search app's
 * `mode`/`status_group`/`operator` are carried across `withSearch`, which
 * otherwise rebuilds its state from a fixed key list and drops them.
 *
 * (1) build a `setSort(sortOn, sortOrder)` callback from the
 * search HOC's `onTriggerSearch` (which re-runs the query AND updates
 * sort state) and pass it into <ListingBody>, which forwards it to the tabular
 * listing variation for working per-column sort. Ports the legacy archives'
 * behaviour. Sibling `./hocs` import rewritten to the absolute `@plone/volto`
 * path (Volto customization rule). The `ListingBody` import resolves to the
 * addon's shadow, which accepts + forwards `setSort`.
 */
const getListingBodyVariation = (data) => {
  const { variations } = config.blocks.blocksConfig.listing;

  let variation = data.listingBodyTemplate
    ? variations.find(({ id }) => id === data.listingBodyTemplate)
    : variations.find(({ isDefault }) => isDefault);

  if (!variation) variation = variations[0];

  return variation;
};

const isfunc = (obj) => isFunction(obj) || typeof obj === 'function';

const _filtered = (obj) =>
  Object.assign(
    {},
    ...Object.keys(obj).map((k) => {
      const reject = k !== 'properties' && !isfunc(obj[k]);
      return reject ? { [k]: obj[k] } : {};
    }),
  );

const blockPropsAreChanged = (prevProps, nextProps) => {
  const prev = _filtered(prevProps);
  const next = _filtered(nextProps);

  return isEqual(prev, next);
};

const applyDefaults = (data, root, blockQuery) => {
  const defaultQuery = [
    {
      i: 'path',
      o: 'plone.app.querystring.operation.string.absolutePath',
      v: root || '/',
    },
  ];

  const dataQuery = data?.query?.length ? data.query : [];
  const searchBySearchableText = dataQuery.filter(
    (item) => item['i'] === 'SearchableText',
  ).length;

  // The no-term fallback comes from `search-registry.json`, not from a literal
  // here. It used to be a hardcoded 'effective' inherited from core, which
  // silently outranked the registry's `sort_defaults.without_term` -- the file
  // that exists to be the one place these values live. (BL-0057)
  //
  // Scope: a block that states its own sort keeps it, and every stored search
  // block in the corpus sorts through its NESTED `query.sort_on` (2,755 of
  // 2,755 have no top-level `sort_on` [block scan, 2026-08-16]), which is why
  // the hubs render docid-ascending rather than by date. In practice this
  // fallback is the Search App's.
  const sort_on = data?.sort_on
    ? { sort_on: data.sort_on }
    : searchBySearchableText === 0
      ? { sort_on: SORT_DEFAULTS.withoutTerm }
      : {};
  const sort_order = data?.sort_order
    ? { sort_order: data.sort_order }
    : searchBySearchableText === 0
      ? { sort_order: SORT_DEFAULTS.withoutTermOrder }
      : {};

  let query = blockQuery?.length ? blockQuery : [];
  if (!query.length) {
    query = dataQuery.length ? dataQuery : defaultQuery;
  } else if (dataQuery.length) {
    const filterKeys = new Set(dataQuery.map((obj) => obj.i));
    query = [
      ...dataQuery,
      ...blockQuery.filter((item) => !filterKeys.has(item.i)),
    ];
  }

  return {
    ...data,
    ...sort_on,
    ...sort_order,
    query,
  };
};

/**
 * Top-level control params the search app sets on the BLOCK, which core drops
 * on the way to the listing.
 *
 * `withSearch` rebuilds its `searchData` from scratch (query rows + sort +
 * b_size + limit + depth + block) — anything else on `data` never reaches
 * `ListingBody`, and therefore never reaches the request. These three are the
 * search app's intent and its two explicit knobs, so they have to be carried
 * across by hand (`correct`, item 21's opt-out, joined them for the same
 * reason). They travel as top-level params rather than querystring rows
 * on purpose: an unknown index in `query` would be handed to
 * plone.app.querystring whenever a request falls through to the stock catalog.
 */
const CONTROL_FIELDS = ['mode', 'status_group', 'operator', 'correct'];

const withControls = (listingData, blockData) =>
  Object.assign(
    {},
    listingData,
    ...CONTROL_FIELDS.map((k) =>
      blockData?.[k] !== undefined ? { [k]: blockData[k] } : {},
    ),
  );

const SearchBlockView = (props) => {
  const { id, data, searchData, mode = 'view', variation, className } = props;

  const Layout = variation.view;

  const dataListingBodyVariation = getListingBodyVariation(data).id;
  const [selectedView, setSelectedView] = React.useState(
    dataListingBodyVariation,
  );

  React.useEffect(() => {
    if (mode !== 'view') {
      setSelectedView(dataListingBodyVariation);
    }
  }, [dataListingBodyVariation, mode]);

  const root = useSelector((state) => state.breadcrumbs.root);

  // GA4 search event: ONE per search, carrying the term a human typed.
  //
  // The term DOES reach the URL — useCanonicalSearchUrl writes it into a
  // `query` parameter as a serialized plone.app.querystring row — and GA4's
  // enhanced measurement watches for a parameter of exactly that name, so it
  // files a second `view_search_results` event whose search term is the raw
  // JSON. That is where the `[{"i":"SearchableText",...}]` rows in the
  // search-terms report come from. It is a data-stream setting, not something
  // this component can suppress; see the ticket. The event below is the clean
  // one and stays the clean one.
  //
  // Deduped on the TERM, not on `term::total` as it was before. A facet
  // applied and removed produces totals A, B, A, which re-fired the same
  // search three times — `nuclear power` recorded 18 events across 5
  // sessions. A refinement is part of one search, not a new one. The count
  // reported is therefore the first one the search returned, which is the
  // number the user actually saw land.
  //
  // The listing's subrequest key is `${content.UID}-${id}`
  // (withQuerystringResults), matched by suffix.
  const searchResults = useSelector((state) => {
    const subs = state.querystringsearch?.subrequests || {};
    const key = Object.keys(subs).find(
      (k) => k === id || k.endsWith(`-${id}`),
    );
    return key ? subs[key] : null;
  });
  const searchedTerm = (props.searchedText || '').trim();
  const lastSearchSig = React.useRef(null);
  React.useEffect(() => {
    if (mode !== 'view' || !searchedTerm) return;
    if (!searchResults?.loaded || searchResults.loading) return;
    if (lastSearchSig.current === searchedTerm) return;
    lastSearchSig.current = searchedTerm;
    const total = searchResults.total ?? 0;
    gaEvent('search', { search_term: searchedTerm, result_count: total });
  }, [mode, searchedTerm, searchResults]);
  const listingBodyData = withControls(
    applyDefaults(searchData, root, data.query?.query),
    data,
  );

  const { variations } = config.blocks.blocksConfig.listing;
  const listingBodyVariation = variations.find(({ id }) => id === selectedView);

  return (
    <div className={cx('block search', selectedView, className)}>
      <Layout
        {...props}
        isEditMode={mode === 'edit'}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
      >
        <ListingBody
          id={id}
          variation={{ ...data, ...listingBodyVariation }}
          data={listingBodyData}
          path={props.path}
          isEditMode={mode === 'edit'}
          // Site scope, for the zero-results panel's escape hatches. It goes
          // through setFacets + onTriggerSearch rather than the URL because
          // core seeds facet state from simple `?site=` params ONLY on the
          // block's first render, and wraps a multi-valued one in a second
          // array — so a URL-driven widen silently does nothing on a page that
          // is already mounted, which is exactly when this button is clicked.
          onSetSites={(sites) => {
            const next = { ...props.facets, site: sites };
            props.setFacets(next);
            props.onTriggerSearch(props.searchedText || '', next);
          }}
          // Drop every field restriction from the current query, for the
          // zero-results escape hatch (items 4 + 18). The term is block state,
          // not a URL control, so it travels the same route as onSetSites.
          onSetSearchText={(text) => {
            props.setSearchText(text);
            props.onTriggerSearch(text, props.facets);
          }}
          setSort={(sortOn, sortOrder) => {
            // onTriggerSearch re-runs the query AND updates sort_on/sort_order
            props.onTriggerSearch(
              props.searchedText || '',
              props.facets,
              sortOn,
              sortOrder,
            );
          }}
        />
      </Layout>
    </div>
  );
};

export const SearchBlockViewComponent = compose(
  withBlockExtensions,
  (Component) => React.memo(Component, blockPropsAreChanged),
)(SearchBlockView);

export default withSearch()(withQueryString(SearchBlockViewComponent));
